import { EnrollmentTier } from "@/lib/utils/enrollment";
import { NStatus } from "./field";
import { FeatureCollection, MultiPolygon } from "geojson";
export type ImagerySubscription = {
    id: number;
    created_by: number;
    last_modified_by: number;
    subscription_id: string;
    provider: number;
    provider_name: string;
    field: number;
    irrigation_system?: number;
    status: number;
};

export type Imagery = {
    id: number;
    created_by: number;
    last_modified_by: number;
    created_date: string;
    imagery_subscription: ImagerySubscription;
    capture_date: string;
    container: string;
    folder: string;
    status: number;
    n_status?: NStatus;
    favorite: boolean;
    flag: boolean;
    apply_date: string;
    recommend_fertigation?: boolean;
    gap?: boolean;
    days_to_apply?: number;
    prescription_id?: number;
    field?: {
        id: number;
        name: string;
        imagery_extent: number;
    };
    model_classification: ModelClassification;
    si_mean: number;
    si_min: number;
    coverage_percent?: number;
    manually_preferred?: boolean;
    active_for_day?: boolean;
    usable?: boolean;
    pending?: boolean;
    provider_name?: string;
    display_urls?: {
        ndre: string;
        ndvi: string;
        rgb: string;
        ccci: string;
        savi: string;
        osavi: string;
        usable_overlay: string;
    };
    use_partial_analysis?: boolean;
    annotated_by?: number | null;
    annotated_by_name?: string | null;
};

export type CocoCategory = {
    id: number;
    name: string;
    supercategory: string;
};

export type CocoImage = {
    id: number;
    width: number;
    height: number;
    file_name: string;
};

export type CocoAnnotation = {
    id: number;
    image_id: number;
    category_id: number;
    segmentation: number[][];
    area: number;
    bbox: [number, number, number, number];
    iscrowd: 0;
    attributes: {
        source: "manual";
    };
};

export type CocoDocument = {
    info: {
        description: string;
        date_created: string;
        annotated_by_user_id: number | null;
    };
    categories: CocoCategory[];
    images: CocoImage[];
    annotations: CocoAnnotation[];
};

export type ImageryAnnotationRequest = {
    annotations: {
        category_id: number;
        segmentation: number[];
    }[];
};

export type ImageryAnnotationResponse = {
    annotation: CocoDocument | null;
    valid_data_boundary: number[][];
};

export type ImageryAnnotationSuggestionModel =
    | "sparrow-one"
    | "sparrow-one-light";

export type ImageryAnnotationSuggestionRequest = {
    model: ImageryAnnotationSuggestionModel;
};

export type ImageryAnnotationSuggestionResponse = {
    model: ImageryAnnotationSuggestionModel;
    image_id: number;
    width: number;
    height: number;
    predictions: {
        category_id: number;
        segmentation: number[];
    }[];
};

export type ImageryDetails = {
    id: number;
    created_by: number;
    last_modified_by: number;
    created_date: string;
    imagery_subscription: ImagerySubscription;
    capture_date: string;
    container: string;
    folder: string;
    prescription_id: null;
    n_status: string;
    recommendation: string;
    percent_sufficient: number;
    ndre_min: number;
    ndre_max: number;
    ndre_mean: number;
    ndre_median: number;
    si_min: number;
    si_max: number;
    si_mean: number;
    si_median: number;
    recommend_fertigation: boolean;
    locked: boolean;
    recent_application: boolean;
    recent_image: boolean;
    crop_mature: boolean;
    contaminants: string[];
    flag: boolean;
    status: number;
    ndvi_min: number;
    ndvi_max: number;
    ndvi_mean: number;
    favorite: boolean;
    viewed_recommendation: null;
    days_to_apply: null;
    pending_application: null;
    apply_date: null;
    bounds: number[];
    display_urls: {
        ndre: string;
        ndvi: string;
        rgb: string;
        si: string;
        savi: string;
        osavi: string;
        ccci: string;
        usable_overlay: string;
    };
    use_partial_analysis: boolean;
};

export enum ModelClassification {
    bad = "bad",
    good = "good",
    flag = "flag",
}

export function getClassificationLabel(classification: ModelClassification) {
    return {
        bad: "Bad",
        good: "Good",
        flag: "Flag",
    }[classification];
}

export type ImageAnalysisProperties = {
    image: number;
    application_section: null;
    section_number: null;
    management_zone: number;
    zone_number: number;
    plot: null | number;
    pair_id: null | number;
    plot_type: string;
    si_mean: null | number;
    si_st_dev: null | number;
    si_max: null | number;
    si_min: null | number;
    si_median: null | number;
    ndre_mean: number;
    ndre_st_dev: number;
    ndre_max: number;
    ndre_min: number;
    ndre_median: number;
    area_rep: number;
    percent_area_rep: number;
    si_status: null | string;
    capture_date: string;
    sensitized_si: null | number;
    is_clear: boolean;
};

export type ImageAnalytics = FeatureCollection<
    MultiPolygon,
    ImageAnalysisProperties
>;

export enum ImageryType {
    NDRE = "NDRE",
    NDVI = "NDVI",
    RGB = "RGB",
    SI = "SI",
    SAVI = "SAVI",
    OSAVI = "OSAVI",
    CCCI = "CCCI",
    SOIL_WATER_CONTENT = "SOIL_WATER_CONTENT",
    LEACHING_RISK = "LEACHING_RISK",
    DROUGHT_RISK = "DROUGHT_RISK",
    CROP_WATER_USE = "CROP_WATER_USE",
    FIELD_HEALTH = "FIELD_HEALTH",
}

export function isWaterImageryType(imageType?: ImageryType) {
    if (!imageType) return false;
    return [
        ImageryType.SOIL_WATER_CONTENT,
        ImageryType.LEACHING_RISK,
        ImageryType.DROUGHT_RISK,
        ImageryType.CROP_WATER_USE,
        ImageryType.FIELD_HEALTH,
    ].includes(imageType);
}

export const IRRIGATION_OPTIONS = [
    { key: ImageryType.SOIL_WATER_CONTENT, label: "Soil Moisture" },
    { key: ImageryType.LEACHING_RISK, label: "Leaching Risk" },
    { key: ImageryType.DROUGHT_RISK, label: "Drought Risk" },
    { key: ImageryType.CROP_WATER_USE, label: "Crop Water Use" },
    { key: ImageryType.FIELD_HEALTH, label: "Crop Health" },
];

export const STANDARD_OPTIONS = [
    { key: ImageryType.NDRE, label: "NDRE" },
    { key: ImageryType.NDVI, label: "NDVI" },
    { key: ImageryType.RGB, label: "RGB" },
];

export const ADVANCED_COMPLETE_OPTIONS = [
    ...STANDARD_OPTIONS,
    { key: ImageryType.SI, label: "SI Map" },
    { key: ImageryType.SAVI, label: "SAVI" },
    { key: ImageryType.OSAVI, label: "OSAVI" },
    { key: ImageryType.CCCI, label: "CCCI" },
];

export const COMPLETE_OPTIONS = [
    ...ADVANCED_COMPLETE_OPTIONS,
    ...IRRIGATION_OPTIONS,
];

export function getImageryTypeOptions(tier: EnrollmentTier) {
    if (
        [
            EnrollmentTier.Advanced,
            EnrollmentTier.AdvancedV2,
            EnrollmentTier.Basic,
        ].includes(tier)
    ) {
        return ADVANCED_COMPLETE_OPTIONS;
    }
    if (tier === EnrollmentTier.Irrigation) {
        return IRRIGATION_OPTIONS;
    }
    if (tier === EnrollmentTier.Complete) {
        return COMPLETE_OPTIONS;
    }
    return STANDARD_OPTIONS;
}

export type ImageryUploadUrlRequest = {
    field_ids: number[];
    capture_date: string;
    band: "NDRE";
};

export type ImageryUploadUrlResponse = {
    image_ids: number[];
    upload_url: string;
    blob_path: string;
    expires_at: string;
    band: string;
};

export type ImageryCompleteUploadRequest = {
    image_ids: number[];
    band: "NDRE";
};

export type ImageryReview = {
    id: number;
    flag: boolean;
    capture_date: string;
    contaminants: string[];
    grower: string;
    service_provider: string;
    account_manager: string;
    provider: string;
    model_classification: ModelClassification;
    si_mean: number;
    si_min: number;
    coverage_percent: number;
};
